import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { interval, Subject, switchMap, takeUntil } from 'rxjs';

import { OrganizationService } from '../../../services/organization.service';
import { NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { AdminService } from '../../../services/admin.service';

// Define interfaces for better type safety
interface WelcomeMessage {
  language: string;
  message: string;
}

interface OrchestrationInstance {
  InstanceId: string;
  RuntimeStatus: string;
  TableStatus?: string;
  ProcessedCount: number;
  TotalRecords: number;
  PendingCount: number;
  Details?: string;
  CreatedAt: string;
  LastUpdatedAt: string;
}


interface SmartAppEnvironment {
  id?: string | null;
  organizationId: string;
  name: string;
  isDefault: boolean;
  smartPhoneConnectionString?: string;
  smartPhonePersonality?: string;
  smartPhoneWelcomeMessage?: string | null | undefined;
  smartPhoneProjectName?: string;
  smartPhoneFirstPrompt?: string | null | undefined;
  smartChatConnectionString?: string;
  smartChatPersonality?: string;
  smartChatWelcomeMessage?: string | null | undefined;
  smartChatProjectName?: string;
  smallTalkProjectName?: string;
  languageServiceApiKey?: string;
  languageServiceEndpoint?: string;
}

@Component({
  selector: 'app-organization-form',
  imports: [CommonModule, FormsModule, NgbTooltipModule],
  templateUrl: './organization-form.component.html',
  styleUrl: './organization-form.component.scss'
})
export class OrganizationFormComponent implements OnInit, OnDestroy {

  @ViewChild('audioPlayer') audioPlayer!: ElementRef<HTMLAudioElement>;
  @ViewChild('orgForm') orgForm!: NgForm; // Reference to the form
  @ViewChild('environmentModal') environmentModal!: any;

  // Organization properties
  isEditMode = false;
  organization: any = {}; // TODO: Define a proper Organization interface
  organizationTypes: any[] = [];

  // Welcome message properties
  smartPhoneWelcomeMessages: WelcomeMessage[] = [];
  smartPhoneFirstPrompts: WelcomeMessage[] = [];
  smartChatWelcomeMessages: WelcomeMessage[] = [];

  // Data seed properties
  isProcessing = false;
  instances: OrchestrationInstance[] = [];
  isLoadingAction = false;
  private destroy$ = new Subject<void>(); // Replaces statusSubscription for cleaner cleanup

  webFile: File | null = null;
  phoneFile: File | null = null;
  smallTalkFile: File | null = null;

  currentEnvironment: SmartAppEnvironment = { id: '', organizationId: '', name: '', isDefault: false };
  isEnvironmentEditMode = false;

  constructor(
    private service: OrganizationService,
    private adminService: AdminService,
    private route: ActivatedRoute,
    private router: Router,
    private modalService: NgbModal) { }

  ngOnInit(): void {
    this.loadOrganizationTypes();

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditMode = true;
      this.loadOrganization(id);
      this.startPolling();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  loadOrganizationTypes() {
    this.service.getOrganizationTypes().subscribe({
      next: (data) => {
        this.organizationTypes = data || [];
      },
      error: (error) => {
        console.error('Error loading organization types:', error);
        alert('Failed to load organization types. Please try again.');
      }
    });
  }

  // Organization management
  private loadOrganization(id: string): void {
    this.service.get(id).subscribe({
      next: (data) => {
        this.organization = data;
        console.log('Loaded organization:', this.organization);
        if (!this.organization.smartAppEnvironments) {
          this.organization.smartAppEnvironments = [];
        }
      },
      error: (error) => {
        console.error('Error loading organization:', error);
        alert('Failed to load organization details. Please try again.');
      }
    });
  }






  //#region Messages

  private parseMessageString(messageString: string | null | undefined): WelcomeMessage[] {
    const messages: WelcomeMessage[] = [];
    if (!messageString?.trim()) return messages;

    const parts = messageString.split('|');
    for (const part of parts) {
      const [language, ...messageParts] = part.split(':');
      const message = messageParts.join(':');
      if (language && message) {
        messages.push({ language, message });
      }
    }
    return messages;
  }

  addWelcomeMessage(messageArray: any[]): void {
    messageArray.push({ language: '', message: '' });
  }

  removeWelcomeMessage(messageArray: any[], index: number): void {
    messageArray.splice(index, 1);
  }

  private formatWelcomeMessages(messages: any[]): string | null {
    if (messages.length === 0) return null;

    return messages
      .filter(msg => msg.language && msg.message)
      .map(msg => `${msg.language}:${msg.message}`)
      .join('|');
  }

  //#endregion

  playText(text: string): void {
    if (!text) return;

    // Use the Web Speech API to read text
    const speech = new SpeechSynthesisUtterance();
    speech.text = text;
    speech.volume = 1;
    speech.rate = 1;
    speech.pitch = 1;

    window.speechSynthesis.speak(speech);
  }




  onSubmit(): void {
    this.isEditMode ? this.updateOrganization() : this.createOrganization();
  }

  private createOrganization(cancelNavigation: boolean = false): void {
    this.service.add(this.organization).subscribe({
      next: () => {
        console.log('Organization created successfully');
        if (cancelNavigation) return; // Prevent navigation if cancelNavigation is true
        this.router.navigate(['/organizations']);
      },
      error: (error) => {
        console.error('Error creating organization:', error);
        alert('Failed to create organization. Please try again.');
      }
    });
  }

  private updateOrganization(cancelNavigation: boolean = false): void {
    if (this.organization.adminGroupId == '')
    {
      this.organization.adminGroupId = null;
    }

    this.service.update(this.organization.id, this.organization).subscribe({
      next: () => {
        console.log('Organization updated successfully');
        if (cancelNavigation) return; // Prevent navigation if cancelNavigation is true
        this.router.navigate(['/organizations']);
      },
      error: (error) => {
        console.error('Error updating organization:', error);
        alert('Failed to update organization. Please try again.');
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/organizations']);
  }




  //#region Environment Management


  openEnvironmentModal(environment?: SmartAppEnvironment): void {
    this.isEnvironmentEditMode = !!environment;
    this.currentEnvironment = environment
      ? { ...environment }
      : { organizationId: this.organization.id, name: '', isDefault: false };

    this.smartPhoneWelcomeMessages = this.parseMessageString(this.currentEnvironment.smartPhoneWelcomeMessage);
    this.smartPhoneFirstPrompts = this.parseMessageString(this.currentEnvironment.smartPhoneFirstPrompt);
    this.smartChatWelcomeMessages = this.parseMessageString(this.currentEnvironment.smartChatWelcomeMessage);
    this.modalService.open(this.environmentModal, { centered: true, size: 'xl' });
  }

  saveEnvironment(): void {
    this.currentEnvironment.smartPhoneWelcomeMessage = this.formatWelcomeMessages(this.smartPhoneWelcomeMessages);
    this.currentEnvironment.smartPhoneFirstPrompt = this.formatWelcomeMessages(this.smartPhoneFirstPrompts);
    this.currentEnvironment.smartChatWelcomeMessage = this.formatWelcomeMessages(this.smartChatWelcomeMessages);

    if (this.currentEnvironment.isDefault) {
      this.organization.smartAppEnvironments.forEach((env: SmartAppEnvironment) => {
        if (env.id !== this.currentEnvironment.id) {
          env.isDefault = false;
        }
      });
    }

    if (this.isEnvironmentEditMode) {
      const index = this.organization.smartAppEnvironments.findIndex(
        (env: SmartAppEnvironment) => env.id === this.currentEnvironment.id
      );
      if (index !== -1) {
        this.organization.smartAppEnvironments[index] = { ...this.currentEnvironment };
      }
    } else {
      this.organization.smartAppEnvironments.push({ ...this.currentEnvironment });
    }

    //this.updateOrganization(true);
    this.modalService.dismissAll();
  }

  
  deleteEnvironment(id: string): void {
    const environment = this.organization.smartAppEnvironments.find((env: SmartAppEnvironment) => env.id === id);
    if (environment.isDefault) {
      alert('Cannot delete the default environment.');
      return;
    }
    const confirmed = confirm(`Are you sure you want to delete the environment "${environment.name}"?`);
    if (!confirmed) return;

    this.organization.smartAppEnvironments = this.organization.smartAppEnvironments.filter(
      (env: SmartAppEnvironment) => env.id !== id
    );
    this.updateOrganization(true);
  }

  applyData(dataType: 'Department' | 'KbItem' , environemntId: string) {

    if( confirm('Are you sure you want to apply the data? This will overwrite existing data in the environment.')) {
      
      this.service.applyDataToEnvironment(this.organization.id, environemntId, dataType, 'ADMIN')
      .subscribe({
        
      });
    }
  }

  //#endregion







  // Data seed orchestration

  private startPolling(): void {
    interval(5000)
      .pipe(
        switchMap(() => this.service.getOrchestrations(this.organization.id)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (instances) => {
          console.log(instances);
          this.instances = instances;
          this.isProcessing = this.instances.some(i => i.RuntimeStatus === 'Running' || i.RuntimeStatus === 'Pending');
        },
        error: (error) => console.error('Error polling instances:', error)
      });
  }

  startDataSeed(): void {
    this.isLoadingAction = true;
    this.isProcessing = true;
    this.service.startDataSeed(this.organization.id).subscribe({
      next: (response) => {
        console.log('Started orchestration:', response);
        this.refresh();
      },
      error: (error) => {
        console.error('Error starting data seed:', error);
        this.isLoadingAction = false;
        this.isProcessing = false; // Reset on error
      },
      complete: () => this.isLoadingAction = false
    });
  }

  provisionSmallTalk(): void {
    const confirmed = confirm('Are you sure you want to provision SmallTalk items for this organization? This will delete all existing SmallTalk knowledge base items.');
    if (!confirmed) return;

    if (this.orgForm.dirty) {
      alert('Please save the form changes before provisioning SmallTalk intents.');
      return;
    }

    this.isLoadingAction = true;
    this.isProcessing = true;
    this.adminService.provisionSmallTalk(this.organization.id).subscribe({
      next: (response) => {
        this.isLoadingAction = false;
        alert(response.Message || 'SmallTalk intents provisioned successfully');
      },
      error: (error) => {
        console.error('Error provisioning SmallTalk:', error);
        this.isLoadingAction = false;
        this.isProcessing = false;
        alert('Failed to provision SmallTalk intents. Please try again.');
      }
    });
  }


  controlInstance(instanceId: string, action: string): void {
    if (action.toLowerCase() === 'terminate') {
      const confirmed = confirm(`Are you sure you want to terminate instance ${instanceId}? This will stop the orchestration.`);
      if (!confirmed) return;
    }

    this.isLoadingAction = true
    this.service.controlOrchestration(instanceId, action).subscribe({
      next: () => {
        console.log(`${action} successful for ${instanceId}`);
        this.refresh();
      },
      error: (error) => {
        console.error(`Error performing ${action}:`, error);
        this.isLoadingAction = false;
      },
      complete: () => this.isLoadingAction = false
    });
  }

  purgeInstance(instanceId: string): void {
    const confirmed = confirm(`Are you sure you want to purge instance ${instanceId}? This will permanently delete all history.`);
    if (!confirmed) return;

    this.isLoadingAction = true;
    this.service.purgeOrchestration(instanceId, this.organization.id).subscribe({
      next: () => {
        console.log(`Purged instance ${instanceId}`);
        this.refresh();
      },
      error: (error) => {
        console.error(`Error purging ${instanceId}:`, error);
        this.isLoadingAction = false;
      },
      complete: () => this.isLoadingAction = false
    });
  }

  refresh(): void {
    this.service.getOrchestrations(this.organization.id).subscribe({
      next: (instances) => {
        this.instances = instances;
        this.isProcessing = this.instances.some(i => i.RuntimeStatus === 'Running' || i.RuntimeStatus === 'Pending');
        console.log('Refreshed instances:', this.instances);
      },
      error: (error) => {
        console.error('Error refreshing instances:', error);
        this.isLoadingAction = false;
      },
      complete: () => this.isLoadingAction = false
    });
  }


  //#region Import/Export

  generateTsv() {
    this.isLoadingAction = true;
    this.adminService.generateTsv(this.organization.id).subscribe({
      next: (blob: Blob) => { // Type Blob explicitly
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = 'export_tsv.zip';
        link.click();
        window.URL.revokeObjectURL(link.href);
        this.isLoadingAction = false;
      },
      error: (error) => {
        console.error('Error generating TSV:', error);
        this.isLoadingAction = false;
        alert('Failed to generate TSV. Please try again.');
      }
    });
  }

  onSmallTalkFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.smallTalkFile = input.files?.[0] || null;
  }

  onWebFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.webFile = input.files?.[0] || null;
  }

  onPhoneFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.phoneFile = input.files?.[0] || null;
  }

  uploadSmallTalkTsv(): void {
    this.uploadTsv(this.smallTalkFile, 'SmallTalk', 'smallTalkFile');
  }

  uploadWebTsv(): void {
    this.uploadTsv(this.webFile, 'Web', 'webFile');
  }

  uploadPhoneTsv(): void {
    this.uploadTsv(this.phoneFile, 'Phone', 'phoneFile');
  }


  @ViewChild('importModal') importModal!: any;

  modalTitle = '';
  modalMessage = '';
  modalErrors: string[] = [];
  modalWarnings: string[] = [];
  totalItems = 0;

  private uploadTsv(file: File | null, importType: 'SmallTalk' | 'Web' | 'Phone', fileProperty: 'smallTalkFile' | 'webFile' | 'phoneFile'): void {
    if (!file || !this.organization.id) {
      this.modalTitle = 'Error';
      this.modalMessage = 'Please select a file and ensure an Organization ID is present';
      this.modalErrors = [];
      this.modalWarnings = [];
      this.totalItems = 0;
      this.modalService.open(this.importModal, { centered: true });
      return;
    }

    this.isLoadingAction = true;
    this.adminService.importTsv(this.organization.id, file, importType).subscribe({
      next: (result: any) => {
        this.isLoadingAction = false;

        this.totalItems = result.totalItems;
        this.modalErrors = result.errors;
        this.modalWarnings = result.warnings;

        if (result.isSuccess) {
          this.modalTitle = 'Success';
          this.modalMessage = `Processed TSV with ${result.totalItems} items, with no errors or warnings`;
        } else if (result.errors.length === 0) {
          this.modalTitle = 'Success with Warnings';
          this.modalMessage = `Processed TSV with ${result.totalItems} items, with ${result.warnings.length} warnings. Check console for details.`;
          console.log(`${importType} TSV Import Warnings:`, result.warnings);
        } else if (result.totalItems > 0) {
          this.modalTitle = 'Partial Success';
          this.modalMessage = `Processed TSV with ${result.totalItems} items, with ${result.errors.length} errors${result.warnings.length > 0 ? ` and ${result.warnings.length} warnings` : ''}. Check console for details.`;
          console.log(`${importType} TSV Import Errors:`, result.errors);
          console.log(`${importType} TSV Import Warnings:`, result.warnings);
        } else {
          this.modalTitle = 'Import Failed';
          this.modalMessage = `Failed to process ${importType} TSV: ${result.errors.length} errors${result.warnings.length > 0 ? ` and ${result.warnings.length} warnings` : ''}. Check console for details.`;
          console.log(`${importType} TSV Import Errors:`, result.errors);
          console.log(`${importType} TSV Import Warnings:`, result.warnings);
        }

        // Reset file input
        this[fileProperty] = null;
        this.refresh();
        this.modalService.open(this.importModal, { centered: true });
      },
      error: (err) => {
        this.isLoadingAction = false;
        this.modalTitle = 'Error';
        this.modalMessage = `Failed to upload ${importType} TSV: ${err.message || 'Unexpected error'}. Check console for details.`;
        this.modalErrors = [err.message || 'Unexpected error'];
        this.modalWarnings = [];
        this.totalItems = 0;
        console.error(`Unexpected error during ${importType} TSV import:`, err);

        // Reset file input
        this[fileProperty] = null;
        this.modalService.open(this.importModal, { centered: true });
      }
    });
  }

  //#endregion

  //#region Integrate AQA

  public integrateAQA(importType: 'SmallTalk' | 'Web' | 'Phone'): void {
    this.adminService.integrateAQA(this.organization.id, importType).subscribe({
      next: (result: any) => { },
      error: (err: any) => {
        console.error('Error integrating AQA:', err);
        alert('Failed to integrate AQA. Please try again.');
      },
    })
  }

  //#endregion

  //#region Intent Map  

  generateIntentMap(): void {
    this.isLoadingAction = true;
    this.adminService.generateIntentMapForOrganization(this.organization.id).subscribe({
      next: (blob: Blob) => { // Type Blob explicitly
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = 'intent_maps.zip';
        link.click();
        window.URL.revokeObjectURL(link.href);
        this.isLoadingAction = false;
      },
      error: (error) => {
        console.error('Error generating Intent Map:', error);
        this.isLoadingAction = false;
        alert('Failed to generate Map. Please try again.');
      }
    });
  }

  integrateWebIntentMap(): void {
    const confirmed = confirm('Are you sure you want to integrate the Web Intent Map into the organization database? This will overwrite any existing web intent map.');
    if (!confirmed) return;

    if (this.orgForm.dirty) {
      alert('Please save the form changes before integrating.');
      return;
    }

    this.isLoadingAction = true;
    this.adminService.integrateIntentMap(this.organization.id!, 'web').subscribe({
      next: () => {
        this.isLoadingAction = false;
        alert('Web Intent Map integrated successfully');
      },
      error: (error) => {
        console.error('Error integrating Web Intent Map:', error);
        this.isLoadingAction = false;
        alert('Failed to integrate Web Intent Map. Please try again.');
      }
    });
  }

  integratePhoneIntentMap(): void {
    const confirmed = confirm('Are you sure you want to integrate the Phone Intent Map into the organization database? This will overwrite any existing phone intent map.');
    if (!confirmed) return;

    if (this.orgForm.dirty) {
      alert('Please save the form changes before integrating.');
      return;
    }


    this.isLoadingAction = true;
    this.adminService.integrateIntentMap(this.organization.id!, 'phone').subscribe({
      next: () => {
        this.isLoadingAction = false;
        alert('Phone Intent Map integrated successfully');
      },
      error: (error) => {
        console.error('Error integrating Phone Intent Map:', error);
        this.isLoadingAction = false;
        alert('Failed to integrate Phone Intent Map. Please try again.');
      }
    });
  }

  //#endregion
}
